import { skipToken } from "@reduxjs/toolkit/query"
import { useMemo } from "react"

import { useGetMasterDataQuery } from "../api/alaskaApi"
import { useGetEventMetadataQuery, useGetMasterEventMetadataQuery } from "../api/reigniteApi"

import { useFirebaseApiSource } from "./useFirebaseApiSource"

type MasterEventApiData = {
  countryName: string
  currentRace: {
    affiliateRaceId: number
    athlinksId: number
    description: string | null
    eventType: string
    extendedDescription: string | null
    facebookUrl: string | null
    hasHeroImage: boolean
    imageUrl: string | null
    instagramHandle: string | null
    isPublishedAthlinks: boolean
    isRosterAvailable: boolean
    location: {
      city: string
      countryId: string
      latitude: number
      longitude: number
      region: string
      timeZone: string
    } | null
    name: string
    organization: {
      name: string
      url: string
    } | null
    startTime: string | number
    siteUri: string | null
    thirdPartyRegLink: string | null
    timer: {
      email: string | null
      name: string
      url: string
    } | null
    twitterHandle: string | null
  }
  eventRaces: {
    azpEventId: number | null
    eventCourses: {
      courseID: number
      courseName: string
      distUnit: number
      eventCourseID: number
    }[]
    isCurrentRace: boolean
    masterEventID: number
    raceID: number
    raceDate: string | number
    raceName: string
  }[]
  masterEventID: number
  name: string
  stateProvAbbrev: string
}

const useMasterEvent = (masterId: number, eventId: number | null) => {
  const { apiSource, isFetchingFirebase } = useFirebaseApiSource(masterId, eventId)

  const isReignite = apiSource === 'reignite'
  const isAlaska = apiSource === 'alaska'

  const {
    data: alaskaData,
    isFetching: isAlaskaFetching,
    error: alaskaError
  } = useGetMasterDataQuery(masterId, { skip: !isAlaska })
  const {
    data: masterReigniteData,
    isFetching: isMasterReigniteFetching,
    error: masterReigniteError,
  } = useGetMasterEventMetadataQuery(masterId, { skip: !isReignite })
  const {
    data: eventReigniteData,
    isFetching: isEventReigniteFetching
  } = useGetEventMetadataQuery(
    masterReigniteData?.currentEvent?.id ?? skipToken, { skip: !isReignite }
  )

  const isFetching = isFetchingFirebase || isAlaskaFetching || isMasterReigniteFetching || isEventReigniteFetching

  const mappedAlaskaData = useMemo(() => {
    if (!isAlaska || !alaskaData?.result) {
      return null
    }

    const { currentRace, eventRaces, ...rest } = alaskaData.result
    const { affiliateRaceId, bazuServices, hasHeroImage, location, ...restCurrentRace } = currentRace

    const mappedData: MasterEventApiData = {
      ...rest,
      currentRace: {
        ...restCurrentRace,
        affiliateRaceId: Number(affiliateRaceId),
        hasHeroImage: !!hasHeroImage,
        isRosterAvailable: bazuServices.find((service) => service.serviceName === "Dirty Startlists")?.available || false,
        location: {
          ...location,
          region: location.regionName,
        }
      },
      eventRaces: eventRaces.map((event) => ({
        ...event,
        azpEventId: Number(event.affiliateLinks[0]?.affiliateRaceID) || null
      })),
    }

    return mappedData
  }, [alaskaData, isAlaska])

  const mappedReigniteData = useMemo(() => {
    if (!isReignite || !masterReigniteData || !eventReigniteData) {
      return null
    }

    const { countryId, events, id, name, stateProvAbbrev } = masterReigniteData
    const { location, organization, timer } = eventReigniteData

    const mappedData: MasterEventApiData = {
      countryName: countryId,
      eventRaces: events.map((event) => ({
        azpEventId: Number(event.azpEventId),
        eventCourses: event.races.map((race) => ({
          courseID: race.id,
          courseName: race.name,
          distUnit: race.distance.meters,
          eventCourseID: race.id,
        })),
        isCurrentRace: masterReigniteData.currentEvent?.id === event.id,
        masterEventID: event.masterId,
        raceID: event.id,
        raceDate: event.start.epoch,
        raceName: event.name,
      })),
      masterEventID: id,
      name,
      stateProvAbbrev,
      currentRace: {
        affiliateRaceId: Number(eventReigniteData.azpEventId),
        athlinksId: eventReigniteData.id,
        description: eventReigniteData.description,
        eventType: eventReigniteData.eventType,
        extendedDescription: eventReigniteData.extendedDescription,
        facebookUrl: eventReigniteData.socialLinks.facebookUrl,
        hasHeroImage: !!eventReigniteData.hasHeroImage,
        imageUrl: eventReigniteData.logoUrl,
        instagramHandle: eventReigniteData.socialLinks.instagramUrl,
        isPublishedAthlinks: eventReigniteData.registration.published,
        isRosterAvailable: eventReigniteData.isRosterAvailable,
        location: location ? {
          city: location.address.city,
          countryId: location.address.countryId,
          latitude: location.latitude,
          longitude: location.longitude,
          region: location.address.region,
          timeZone: location.timeZone,
        } : null,
        name: eventReigniteData.name,
        organization: organization ? {
          name: organization.name,
          url: organization.url,
        } : null,
        startTime: eventReigniteData.start.epoch,
        siteUri: eventReigniteData.socialLinks.websiteUrl,
        thirdPartyRegLink: eventReigniteData.thirdPartyRegLink,
        timer: timer ? {
          email: timer.email,
          name: timer.name,
          url: timer.url,
        } : null,
        twitterHandle: eventReigniteData.socialLinks.twitterUrl,
      },
    }

    return mappedData
  }, [isReignite, masterReigniteData, eventReigniteData])

  const response = useMemo<MasterEventApiData | null>(() => {
    if (isReignite) {
      return mappedReigniteData
    }
    if (isAlaska) {
      return mappedAlaskaData
    }
    return null
  }, [isAlaska, isReignite, mappedAlaskaData, mappedReigniteData])

  const error = useMemo(() => {
    if (masterReigniteError) {
      const typedError = masterReigniteError as { status: number; data: { message: string } }

      if (typedError.data.message.startsWith('No master event found')){
        return {
          status: 404
        }
      }

      return { status: 500 }
    }
    if (alaskaError) {
      const typedError = alaskaError as { status: number; data: { ErrorMessage: string } }

      return { status: typedError.status }
    }

    return null
  }, [alaskaError, masterReigniteError])

  return {
    isMasterEventFetching: isFetching,
    masterEvent: response,
    masterEventError: error,
  }
}

export { useMasterEvent }

export type { MasterEventApiData }